import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';

export default function CookiePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="UniSide Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-bold font-outfit">UniSide</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="border-slate-100 shadow-sm rounded-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Cookie className="w-8 h-8 text-primary" />
              <CardTitle className="text-3xl font-outfit">Cookie-Richtlinie</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-6">
              <strong>Stand: Februar 2026</strong>
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">1. Was sind Cookies?</h3>
            <p>
              Cookies sind kleine Textdateien, die von Websites auf Ihrem Gerät gespeichert werden. 
              Sie helfen dabei, Ihre Einstellungen zu speichern und die Website-Nutzung zu verbessern.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">2. Welche Cookies verwenden wir?</h3>
            
            <h4 className="text-lg font-semibold mt-4 mb-2">2.1 Technisch notwendige Cookies</h4>
            <p>
              Diese Cookies sind für den Betrieb der Website unbedingt erforderlich. Dazu gehören:
            </p>
            <table className="w-full border-collapse border border-slate-300 my-4">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-300 p-2 text-left">Cookie-Name</th>
                  <th className="border border-slate-300 p-2 text-left">Zweck</th>
                  <th className="border border-slate-300 p-2 text-left">Speicherdauer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 p-2"><code>token</code></td>
                  <td className="border border-slate-300 p-2">Authentifizierung (Login-Status)</td>
                  <td className="border border-slate-300 p-2">Sitzung / 7 Tage</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2"><code>cookieConsent</code></td>
                  <td className="border border-slate-300 p-2">Speichert Ihre Cookie-Einwilligung</td>
                  <td className="border border-slate-300 p-2">1 Jahr</td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-lg font-semibold mt-4 mb-2">2.2 Keine Tracking-Cookies</h4>
            <p>
              <strong>Wichtig:</strong> UniSide verwendet <strong>keine</strong> Tracking-Cookies, 
              Analyse-Tools (wie Google Analytics) oder Werbe-Cookies. Wir respektieren Ihre Privatsphäre 
              und sammeln nur die Daten, die für den Betrieb der Plattform notwendig sind.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">3. Rechtsgrundlage</h3>
            <p>
              Die Verwendung technisch notwendiger Cookies erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f 
              DSGVO (berechtigtes Interesse). Diese Cookies sind für die Grundfunktionen der Website 
              erforderlich und können nicht deaktiviert werden.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">4. Cookies verwalten</h3>
            <p>
              Sie können Cookies in Ihren Browser-Einstellungen verwalten oder löschen:
            </p>
            <ul className="list-disc pl-6 my-4">
              <li><strong>Chrome:</strong> Einstellungen → Datenschutz und Sicherheit → Cookies</li>
              <li><strong>Firefox:</strong> Einstellungen → Datenschutz & Sicherheit → Cookies</li>
              <li><strong>Safari:</strong> Einstellungen → Datenschutz → Cookies verwalten</li>
              <li><strong>Edge:</strong> Einstellungen → Datenschutz → Cookies</li>
            </ul>
            <p className="text-amber-700 bg-amber-50 p-4 rounded-lg">
              <strong>Hinweis:</strong> Das Blockieren aller Cookies kann dazu führen, dass bestimmte 
              Funktionen (wie das Einloggen) nicht mehr funktionieren.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">5. Local Storage</h3>
            <p>
              Neben Cookies verwenden wir auch den Local Storage Ihres Browsers. Dort speichern wir:
            </p>
            <ul className="list-disc pl-6 my-4">
              <li><strong>token:</strong> Ihr Authentifizierungs-Token für den Login</li>
              <li><strong>cookieConsent:</strong> Ihre Zustimmung zur Cookie-Verwendung</li>
            </ul>
            <p>
              Der Local Storage wird gelöscht, wenn Sie sich ausloggen oder die Browser-Daten löschen.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">6. Änderungen dieser Richtlinie</h3>
            <p>
              Wir behalten uns vor, diese Cookie-Richtlinie bei Bedarf zu aktualisieren. 
              Änderungen werden auf dieser Seite veröffentlicht.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">7. Kontakt</h3>
            <p>
              Bei Fragen zu Cookies wenden Sie sich bitte an:
            </p>
            <p>
              <strong>Verein Zoro</strong><br />
              E-Mail: datenschutz@uniside.at<br />
              Telefon: +43/699 17070547
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
